/**
 * Timeout and duration constants
 */

/** Debounce delay for search inputs (ms) */
export const SEARCH_DEBOUNCE_MS = 300;

/** Polling interval for status checks (ms) */
export const POLLING_INTERVAL_MS = 5000;

/** Fast polling interval for export status checks (ms) */
export const EXPORT_POLLING_INTERVAL_MS = 2500;

/** Polling interval for AI analysis progress checks (ms) */
export const AI_ANALYSIS_POLLING_INTERVAL_MS = 2000;

/**
 * Show a non-blocking "this is taking longer than usual" banner once analysis
 * has been polling for this long without finishing or failing. Past the BE's
 * worst-case happy path (≈3min per-file analysis + ≈3min consolidation), but
 * before the hard fail.
 */
export const AI_ANALYSIS_STUCK_WARN_MS = 4 * 60 * 1000;

/**
 * Treat the whole analysis run as failed client-side after this long. Guards
 * against the BE never reporting `failed` (network drop, BE deploy lag, etc).
 * Timer runs from the first poll — not just the consolidating phase — so a
 * file stuck in PROCESSING still trips the timeout.
 */
export const AI_ANALYSIS_STUCK_FAIL_MS = 7 * 60 * 1000;

/**
 * If consolidation progress doesn't change (no new step, no progress %
 * increase) for this long, show a reassurance line below the progress UI
 * (LF-3367).
 */
export const AI_ANALYSIS_STEP_STALL_MS = 30 * 1000;

/**
 * How long the analysing surface rests on its completion state before the
 * caller is allowed to navigate on (VALE-1232).
 *
 * Without a hold there is no completion moment at all: `done()` sets
 * `isComplete` and resolves in the same tick, and both callers do
 * `await done(); setCurrentStep('review')`, so React commits the step change
 * in the same batch and the finished state (green mark, squared pile, "Your
 * matter is ready") is never painted. The reader watches a wait of minutes
 * and is then moved on with no sign it succeeded.
 *
 * 2.2 s, raised from the prototype's 1.2 s rest after the owner reported not
 * seeing the completion state at all on a real run: the tick lands, the stack
 * squares up and the line goes green, and 1.2 s covered the animation without
 * leaving anything to look at. Still nothing against a wait measured in
 * minutes.
 */
export const AI_ANALYSIS_COMPLETION_HOLD_MS = 2200;

/**
 * How long each consolidation check is shown before the next takes over
 * (VALE-1232).
 *
 * **Constant, not randomised.** It used to be 1200ms +/- 40%, which gave a
 * 720-1680ms jitter that read as the surface stumbling rather than working.
 * These steps are a client-side cadence either way, so randomising bought no
 * honesty and cost the rhythm.
 *
 * The steps are paced, not timed to real work, so the only truthful signal
 * left is stopping: when the list runs out and the backend has not finished,
 * the ledger holds on the last step and the long-wait notice takes over. That
 * pause is the one thing here that means something, and jitter was noise on
 * top of it.
 */
export const AI_ANALYSIS_CONSOLIDATION_STEP_MS = 1200;

/** Maximum polling duration for exports before timeout (ms) - 20 seconds */
export const EXPORT_POLLING_TIMEOUT_MS = 20000;

/** Debounce delay for persisting user preferences (ms) */
export const PREFERENCE_SAVE_DEBOUNCE_MS = 500;

/**
 * Title-search optimistic-window threshold (ms) — Damo feedback item #1,
 * "Build optimistic, fail soft".
 *
 * Inside this window, the row-indicator shows the inline "Searching…"
 * spinner and the user waits inline for the result. Past the threshold,
 * copy switches to "Search in progress. We'll notify you when ready." and
 * the backend's `TitleSearchNotificationListener` starts firing in-app +
 * email notifications.
 *
 * Coordinated with `TITLE_SEARCH_NOTIFICATION_THRESHOLD_MS` on the BE
 * (default `20000`, configurable via env var — see BE-8 / LF-3620
 * handover). The FE constant and the BE constant must move in lockstep:
 * if you tune one, tune the other.
 */
export const TITLE_SEARCH_OPTIMISTIC_WINDOW_MS = 20000;

/**
 * Title-search in-flight poll interval (ms) — graceful-degradation fallback
 * when the in-app notification subscription is offline.
 *
 * Activated by `useTitleSearchesForMatter` only when at least one row in
 * the cached list is in an in-flight state (`in_progress`); otherwise the
 * query is idle. Sized to match the brief: "polling fallback on the item
 * row's query (e.g., refetch every 60s while the row is in an in-flight
 * state)" — FE-14 (LF-3626) brief.
 */
export const TITLE_SEARCH_INFLIGHT_POLL_INTERVAL_MS = 10000;
/** Polling interval for the matter sidebar's AI Data Capture indicator
 *  (LF-3045) — only used while a lane has in-progress items. */
export const AI_DATA_CAPTURE_POLL_INTERVAL_MS = 3 * 60 * 1000;

/**
 * PMS contact-binding in-flight poll interval (ms) — the matter contacts
 * listing's binding indicator (VALE-159).
 *
 * Activated by `usePmsMatterContactBindings` only while at least one binding in
 * the cached list is still in flight (recorded, not yet filed, no error);
 * a listing whose rows have all settled is idle and pays nothing.
 */
export const PMS_CONTACT_BINDING_INFLIGHT_POLL_INTERVAL_MS = 5000;

/**
 * How many in-flight polls the bindings query makes before giving up (VALE-159).
 *
 * **A ceiling, not a timeout sized for a slow PMS.** An in-flight binding is not
 * guaranteed to be transient: the matter-sync path deliberately skips the bind
 * for a contact that is not itself synced to the PMS yet, leaving that row
 * `isBound: false` with no error until the contact lands there — which may be
 * never. Without a ceiling a single such row would poll for as long as the tab
 * stays open. A bind that is genuinely in progress settles well inside this.
 */
export const PMS_CONTACT_BINDING_MAX_INFLIGHT_POLLS = 24;
